import { useEffect, useRef } from 'react';

import { makeLabelComparator, sortDisplayStrings } from './../../../../shared-web/utilities/sort-options.js';
import { formatDateChipValue } from './date-filter-helpers.js';
import { formatAmountChipValue } from './amount-filter-helpers.js';

/**
 * Collect a field's values for one item, expanding tag fields into individual tags.
 * @param {{ getTags?: (item: Record<string, any>) => string[], getValue: (item: Record<string, any>) => any }} field
 * @param {Record<string, any>} item
 * @param {Set<string>} out
 * @returns {void}
 */
export function collectFieldValues(field, item, out) {
	if (field.getTags) {
		for (const tag of field.getTags(item)) { out.add(tag); }
		return;
	}
	out.add(String(field.getValue(item)));
}

/**
 * Build combobox field options from filterFields.
 * @param {Array<{ key: string, label: string, type: string, options?: string[] }>} filterFields
 * @param {(key: string) => string} t
 * @returns {Array<{ key: string, label: string, type: string, options?: string[] }>}
 */
export function buildFieldOptions(filterFields, t) {
	/** @type {Array<{ key: string, label: string, type: string, options?: string[] }>} */
	const opts = [{ key: '*', label: t('filter.allFields'), type: 'text' }];
	for (const f of filterFields) {
		opts.push({ key: f.key, label: f.label, type: f.type, options: f.options });
	}
	return opts;
}

/**
 * Build select options for current field.
 * @param {{ key: string, label: string, type: string, options?: string[] } | null} currentField
 * @param {Array<{ key: string, label: string, type: string, options: string[], getValue: (item: any) => string }>} filterFields
 * @param {any[]} items
 * @param {string} language
 * @returns {string[]}
 */
export function buildSelectOptions(currentField, filterFields, items, language) {
	if (!currentField || currentField.key === '*') { return []; }
	if (currentField.options && currentField.options.length > 0) {
		return [...currentField.options].sort((a, b) => {
			const al = typeof a === 'object' ? a.label : a;
			const bl = typeof b === 'object' ? b.label : b;
			return makeLabelComparator(language)(al, bl);
		});
	}
	if (currentField.type === 'select') {
		const field = filterFields.find((f) => f.key === currentField?.key);
		if (!field) { return []; }
		const vals = new Set();
		for (const item of items) {
			try { collectFieldValues(field, item, vals); } catch { /* skip */ }
		}
		return sortDisplayStrings([...vals], language);
	}
	return [];
}

/**
 * Resolve display value for a chip option.
 * @param {{ type?: string, options?: Array<string | { value: string, label: string }> } | null} field
 * @param {string} value
 * @param {(key: string, opts?: any) => string} [t]
 * @param {string} [dateFormat]
 * @returns {string}
 */
export function getChipDisplayValue(field, value, t, dateFormat) {
	if (field?.type === 'date' && t) {
		return formatDateChipValue(value, t, dateFormat ?? 'iso');
	}
	if (field?.type === 'currency' && t) {
		return formatAmountChipValue(value, t);
	}
	const opt = field?.options?.find((o) => (typeof o === 'object' ? o.value : o) === value);
	if (opt && typeof opt === 'object') { return opt.label; }
	return value;
}

/**
 * Handle open state reset for filter bar.
 * @param {boolean} open
 * @param {number | null} editingIndex
 * @param {(v: string) => void} setSelectedField
 * @param {(v: string) => void} setInputValue
 * @returns {void}
 */
export function useFilterOpenEffect(open, editingIndex, setSelectedField, setInputValue) {
	const prevOpen = useRef(false);
	useEffect(() => {
		if (open && !prevOpen.current) {
			if (editingIndex === null) {
				setSelectedField('*');
				setInputValue('');
			}
		}
		prevOpen.current = open;
	}, [open, editingIndex, setSelectedField, setInputValue]);
}

/**
 * Focus management for filter value inputs.
 * @param {{ open: boolean, selectedField: string, isSelect: boolean, valueSelectRef: React.RefObject<HTMLInputElement | null>, valuePickerRef: React.RefObject<{ open: () => void } | null>, valueInputRef: React.RefObject<HTMLInputElement | null> }} opts
 * @returns {void}
 */
export function useFilterFocus(opts) {
	const { open, selectedField, isSelect, valueSelectRef, valuePickerRef, valueInputRef } = opts;
	useEffect(() => {
		if (!open || !selectedField || !isSelect) { return () => {}; }
		valueSelectRef.current?.focus();
		const id = window.setTimeout(() => {
			valuePickerRef.current?.open();
		}, 0);
		return () => window.clearTimeout(id);
	}, [open, selectedField, isSelect, valueSelectRef, valuePickerRef]);
	useEffect(() => {
		if (open && selectedField && !isSelect && valueInputRef.current) {
			valueInputRef.current.focus();
		}
	}, [open, selectedField, isSelect, valueInputRef]);
}

/**
 * Create filter clause handlers.
 * @param {{ clauses: Array<{ fieldKey: string, value: string }>, setClauses: (v: Array<{ fieldKey: string, value: string }>) => void, onFilterChange: (v: Array<{ fieldKey: string, value: string }>) => void, setEditingIndex: (v: number | null) => void, setSelectedField: (v: string) => void, setInputValue: (v: string) => void, onToggle: (v: boolean) => void, selectedField: string, inputValue: string, editingIndex: number | null, fieldOptions: Array<{ key: string, label: string, type: string, options?: string[] }> }} opts
 * @returns {{ removeClause: (c: { fieldKey: string, value: string }) => void, clearAll: () => void, applyFilter: () => void, handleFieldChange: (k: string) => void, handleChipClick: (c: { fieldKey: string, value: string }, i: number) => void, handleAddNew: () => void, handleCancel: () => void }}
 */
export function createFilterHandlers(opts) {
	const { clauses, setClauses, onFilterChange, setEditingIndex, setSelectedField, setInputValue, onToggle, selectedField, inputValue, editingIndex, fieldOptions } = opts;
	function removeClause(clause) {
		const next = clauses.filter((c) => c !== clause);
		setClauses(next);
		onFilterChange(next);
		if (next.length === 0) {
			setEditingIndex(null);
			setSelectedField('*');
			setInputValue('');
			onToggle(true);
		}
	}
	function clearAll() {
		setClauses([]);
		setEditingIndex(null);
		onFilterChange([]);
	}
	function applyFilter() {
		if (!selectedField || !inputValue) { return; }
		let next;
		if (editingIndex !== null) {
			next = clauses.map((c, i) => i === editingIndex ? { fieldKey: selectedField, value: inputValue } : c);
		} else {
			next = [...clauses, { fieldKey: selectedField, value: inputValue }];
		}
		setClauses(next);
		onFilterChange(next);
		setSelectedField('');
		setInputValue('');
		setEditingIndex(null);
		onToggle(false);
	}
	function handleFieldChange(key) {
		setSelectedField(key);
		setInputValue('');
		const opt = fieldOptions.find((f) => f.key === key);
		if (opt && opt.type === 'select' && opt.options && opt.options.length === 1) {
			setInputValue(opt.options[0]);
		}
	}
	function handleChipClick(clause, index) {
		setEditingIndex(index);
		setSelectedField(clause.fieldKey);
		setInputValue(clause.value);
		onToggle(true);
	}
	function handleAddNew() {
		setEditingIndex(null);
		setSelectedField('*');
		setInputValue('');
		onToggle(true);
	}
	function handleCancel() {
		setSelectedField('');
		setInputValue('');
		setEditingIndex(null);
		onToggle(false);
	}
	return { removeClause, clearAll, applyFilter, handleFieldChange, handleChipClick, handleAddNew, handleCancel };
}
